import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './WarEffortSpoilsDialog.css';
import { API_URL } from './config/apiConfig';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { playSoundEffect } from './hooks/useSoundEffects';

function WarEffortSpoilsDialog({ isOpen, onClose, userId, previewMode = false }) {
  const { i18n: i18nInstance } = useTranslation();
  const [spoils, setSpoils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [grantedOrders, setGrantedOrders] = useState([]); // Store granted orders for display
  const [revealedRewards, setRevealedRewards] = useState([]); // Track which rewards are revealed
  const [enrichedRewards, setEnrichedRewards] = useState([]); // Store full order data
  const videoRefs = useRef([]);
  const didPlayHiddenRef = useRef(false);

  useEffect(() => {
    if (isOpen && userId) {
      if (previewMode) {
        fetchPreview();
      } else {
        fetchSpoils();
      }
    }
  }, [isOpen, userId, previewMode]);

  // Play hidden_rewards when flip-to-reveal grid is first shown (all cards face-down)
  useEffect(() => {
    if (grantedOrders.length === 0) {
      didPlayHiddenRef.current = false;
      return;
    }
    if (grantedOrders.length > 0 && revealedRewards.length === grantedOrders.length && revealedRewards.every(r => !r)) {
      if (!didPlayHiddenRef.current) {
        playSoundEffect('/sounds/hidden_rewards.mp3');
        didPlayHiddenRef.current = true;
      }
    }
  }, [grantedOrders.length, revealedRewards]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/preview-season-end`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Format preview data to match spoils format
        setSpoils([{
          faction_name: data.factionName,
          contribution_percentage: data.contributionPercentage,
          total_orders_earned: data.ordersEarned,
          common_orders: data.commonOrders,
          rare_orders: data.rareOrders,
          epic_orders: data.epicOrders,
          legendary_orders: data.legendaryOrders,
          preview: true,
          victories: data.victories,
          days_in_team: data.daysInTeam,
          is_winning_team: data.isWinningTeam,
          total_players: data.totalPlayers
        }]);
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpoils = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/spoils`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSpoils(data);
      }
    } catch (error) {
      console.error('Error fetching spoils:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimSpoils = async () => {
    if (claiming || spoils.length === 0) return;

    setClaiming(true);
    setClaimResult(null);
    setGrantedOrders([]);
    setRevealedRewards([]);
    setEnrichedRewards([]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/claim-spoils`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setClaimResult(result);
        
        // If orders were returned, set them up for display
        if (result.orders && Array.isArray(result.orders) && result.orders.length > 0) {
          setGrantedOrders(result.orders);
          setRevealedRewards(new Array(result.orders.length).fill(false));
          setEnrichedRewards(new Array(result.orders.length).fill(null));
          videoRefs.current = new Array(result.orders.length).fill(null);
        }
        
        // Refresh spoils after claiming
        await fetchSpoils();
      } else {
        const error = await response.json();
        setClaimResult({ error: error.error || 'Failed to claim spoils' });
      }
    } catch (error) {
      console.error('Error claiming spoils:', error);
      setClaimResult({ error: 'Failed to claim spoils' });
    } finally {
      setClaiming(false);
    }
  };

  const handleSimulateClaimSpoils = () => {
    if (spoils.length === 0) return;

    setClaiming(true);
    setClaimResult(null);
    setGrantedOrders([]);
    setRevealedRewards([]);
    setEnrichedRewards([]);

    // Simulate API delay
    setTimeout(() => {
      // Create simulated claim result based on preview spoils
      const spoil = spoils[0];
      
      // Create mock order objects for preview
      const mockOrders = [];
      for (let i = 0; i < (spoil.common_orders || 0); i++) {
        mockOrders.push({ id: `mock-common-${i}`, name: 'Common Order', rarity: 'common' });
      }
      for (let i = 0; i < (spoil.rare_orders || 0); i++) {
        mockOrders.push({ id: `mock-rare-${i}`, name: 'Rare Order', rarity: 'rare' });
      }
      for (let i = 0; i < (spoil.epic_orders || 0); i++) {
        mockOrders.push({ id: `mock-epic-${i}`, name: 'Epic Order', rarity: 'epic' });
      }
      for (let i = 0; i < (spoil.legendary_orders || 0); i++) {
        mockOrders.push({ id: `mock-legendary-${i}`, name: 'Legendary Order', rarity: 'legendary' });
      }
      
      const simulatedResult = {
        claimed: true,
        granted: {
          common: spoil.common_orders || 0,
          rare: spoil.rare_orders || 0,
          epic: spoil.epic_orders || 0,
          legendary: spoil.legendary_orders || 0
        },
        totalGranted: spoil.total_orders_earned || 0,
        orders: mockOrders
      };

      console.log('Simulating claim spoils:', simulatedResult);
      setClaimResult(simulatedResult);
      
      // Set up orders for display
      if (mockOrders.length > 0) {
        setGrantedOrders(mockOrders);
        setRevealedRewards(new Array(mockOrders.length).fill(false));
        setEnrichedRewards(new Array(mockOrders.length).fill(null));
        videoRefs.current = new Array(mockOrders.length).fill(null);
      }
      
      setClaiming(false);
    }, 500); // Simulate 500ms API delay
  };

  if (!isOpen) return null;

  const totalOrders = spoils.reduce((sum, s) => sum + (s.total_orders_earned || 0), 0);
  const totalCommon = spoils.reduce((sum, s) => sum + (s.common_orders || 0), 0);
  const totalRare = spoils.reduce((sum, s) => sum + (s.rare_orders || 0), 0);
  const totalEpic = spoils.reduce((sum, s) => sum + (s.epic_orders || 0), 0);
  const totalLegendary = spoils.reduce((sum, s) => sum + (s.legendary_orders || 0), 0);

  return (
    <div className="spoils-dialog-overlay" onClick={onClose}>
      <div className="spoils-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="spoils-dialog-header">
          <h2>
            {previewMode ? '🔍 Season End Preview (TESTING)' : 'War Effort Spoils'}
          </h2>
          <button className="spoils-dialog-close" onClick={onClose}>×</button>
        </div>

        <div className="spoils-dialog-content">
          {previewMode && spoils.length > 0 && (
            <div className="spoils-preview-warning">
              <p>⚠️ This is a TEST PREVIEW - no rewards will be granted!</p>
              {spoils[0].victories !== undefined && (
                <div className="spoils-preview-stats">
                  <p>Your Stats: {spoils[0].victories} victories, {spoils[0].days_in_team} days in team</p>
                  <p>Total Players: {spoils[0].total_players}</p>
                  <p>Team Status: {spoils[0].is_winning_team ? '🏆 WINNING' : '❌ LOSING'}</p>
                </div>
              )}
            </div>
          )}
          {loading ? (
            <div className="spoils-loading">Loading spoils...</div>
          ) : spoils.length === 0 ? (
            <div className="spoils-empty">
              <p>No unclaimed spoils available.</p>
              <p className="spoils-empty-hint">Spoils are calculated when a season ends.</p>
            </div>
          ) : (
            <>
              <div className="spoils-summary">
                <h3>{previewMode ? 'Projected Rewards' : 'Total Spoils'}</h3>
                <div className="spoils-summary-grid">
                  <div className="spoils-summary-item">
                    <span className="spoils-label">Total Orders:</span>
                    <span className="spoils-value">{totalOrders}</span>
                  </div>
                  <div className="spoils-summary-item">
                    <span className="spoils-label">Common:</span>
                    <span className="spoils-value common">{totalCommon}</span>
                  </div>
                  <div className="spoils-summary-item">
                    <span className="spoils-label">Rare:</span>
                    <span className="spoils-value rare">{totalRare}</span>
                  </div>
                  <div className="spoils-summary-item">
                    <span className="spoils-label">Epic:</span>
                    <span className="spoils-value epic">{totalEpic}</span>
                  </div>
                  <div className="spoils-summary-item">
                    <span className="spoils-label">Legendary:</span>
                    <span className="spoils-value legendary">{totalLegendary}</span>
                  </div>
                </div>
              </div>

              <div className="spoils-breakdown">
                <h3>Breakdown by Season</h3>
                {spoils.map((spoil, index) => (
                  <div key={index} className="spoils-item">
                    <div className="spoils-item-header">
                      <span className="spoils-faction">{spoil.faction_name}</span>
                      <span className="spoils-contribution">
                        {parseFloat(spoil.contribution_percentage || 0).toFixed(2)}% contribution
                      </span>
                    </div>
                    <div className="spoils-item-details">
                      <div className="spoils-orders-breakdown">
                        <span className="spoils-order-type common">Common: {spoil.common_orders || 0}</span>
                        <span className="spoils-order-type rare">Rare: {spoil.rare_orders || 0}</span>
                        <span className="spoils-order-type epic">Epic: {spoil.epic_orders || 0}</span>
                        <span className="spoils-order-type legendary">Legendary: {spoil.legendary_orders || 0}</span>
                      </div>
                      <div className="spoils-total">
                        Total: {spoil.total_orders_earned || 0} orders
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {!previewMode && claimResult && (
                <div 
                  className={`spoils-claim-result ${claimResult.error ? 'error' : 'success'}`}
                  style={{ 
                    marginTop: '20px',
                    marginBottom: '20px',
                    padding: '20px',
                    borderRadius: '8px',
                    border: claimResult.error ? '2px solid rgba(200, 0, 0, 0.8)' : '2px solid rgba(0, 200, 0, 0.8)',
                    backgroundColor: claimResult.error ? 'rgba(200, 0, 0, 0.3)' : 'rgba(0, 200, 0, 0.3)',
                    color: '#fff',
                    fontSize: '16px'
                  }}
                >
                  {claimResult.error ? (
                    <p>❌ {claimResult.error}</p>
                  ) : (
                    <>
                      <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                        ✅ Spoils claimed successfully!
                      </p>
                      {claimResult.totalGranted !== undefined && (
                        <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                          <strong>Total Orders Granted: {claimResult.totalGranted}</strong>
                        </p>
                      )}
                      {claimResult.granted && (
                        <div className="spoils-granted-details">
                          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Granted by Rarity:</p>
                          <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>Common: {claimResult.granted.common || 0}</li>
                            <li>Rare: {claimResult.granted.rare || 0}</li>
                            <li>Epic: {claimResult.granted.epic || 0}</li>
                            <li>Legendary: {claimResult.granted.legendary || 0}</li>
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {!previewMode && claimResult && claimResult.error && (
                <div 
                  className="spoils-claim-result error"
                  style={{ 
                    marginTop: '20px',
                    marginBottom: '20px',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '2px solid rgba(200, 0, 0, 0.8)',
                    backgroundColor: 'rgba(200, 0, 0, 0.3)',
                    color: '#fff',
                    fontSize: '16px'
                  }}
                >
                  <p>❌ {claimResult.error}</p>
                </div>
              )}
              
              {/* Reward Cards Display for Real Mode */}
              {!previewMode && grantedOrders.length > 0 && (
                <div style={{ marginTop: '30px', marginBottom: '20px' }}>
                  <h3 style={{ color: '#fff', textAlign: 'center', marginBottom: '20px', fontSize: '20px' }}>
                    Click cards to reveal your rewards!
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '40px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: '860px', // 2 cards (390px each) + gap (40px) + padding
                    margin: '0 auto'
                  }}>
                    {grantedOrders.map((order, index) => {
                      const isRevealed = revealedRewards[index];
                      const enrichedOrder = enrichedRewards[index];
                      const rarity = order.rarity?.toLowerCase() || 'common';
                      
                      return (
                        <div
                          key={order.id || index}
                          onClick={async () => {
                            if (!isRevealed) {
                              setRevealedRewards(prev => {
                                const newState = [...prev];
                                newState[index] = true;
                                return newState;
                              });
                              const isLegendary = (order.rarity || '').toLowerCase() === 'legendary';
                              playSoundEffect(isLegendary ? '/sounds/legendary_reveal.mp3' : '/sounds/order_reveal.mp3');
                              
                              // Fetch full order data
                              if (order.id) {
                                const token = localStorage.getItem('token');
                                try {
                                  const res = await fetch(`${API_URL}/all-orders?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
                                    headers: {
                                      Authorization: `Bearer ${token}`
                                    }
                                  });
                                  if (res.ok) {
                                    const allOrders = await res.json();
                                    const fullOrder = allOrders.find(o => o.id === order.id);
                                    if (fullOrder) {
                                      fullOrder.rarity = order.rarity || fullOrder.rarity;
                                      setEnrichedRewards(prev => {
                                        const newState = [...prev];
                                        newState[index] = fullOrder;
                                        return newState;
                                      });
                                    }
                                  }
                                } catch (err) {
                                  console.error('Failed to fetch full order data:', err);
                                }
                              }
                            }
                          }}
                          style={{
                            width: '390px',
                            height: '390px',
                            margin: 0,
                            padding: 0,
                            perspective: '1000px',
                            cursor: !isRevealed ? 'pointer' : 'default',
                            position: 'relative',
                            overflow: 'visible',
                            flexShrink: 0
                          }}
                        >
                          <div
                            style={{
                              width: '390px',
                              height: '390px',
                              position: 'relative',
                              transformStyle: 'preserve-3d',
                              transition: 'transform 0.6s',
                              transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'
                            }}
                          >
                            {/* Front side - Question mark with video */}
                            <div style={{
                              position: 'absolute',
                              width: '390px',
                              height: '390px',
                              left: '50%',
                              transform: 'translateX(-50%) rotateY(0deg)',
                              padding: '20px 5px',
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              border: '2px solid #C0C0C0',
                              borderRadius: '8px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              boxSizing: 'border-box',
                              backfaceVisibility: 'hidden',
                              WebkitBackfaceVisibility: 'hidden',
                              overflow: 'hidden',
                              boxShadow: '0 0 8px rgba(200, 200, 200, 0.6), inset 0 0 10px rgba(255, 255, 255, 0.1)'
                            }}>
                              <video
                                ref={el => { videoRefs.current[index] = el; }}
                                poster="/images/reward_intel.jpg"
                                autoPlay
                                loop
                                muted
                                playsInline
                                style={{
                                  position: 'absolute',
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  top: 0,
                                  left: 0,
                                  zIndex: 1,
                                  pointerEvents: 'none',
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)'
                                }}
                                onCanPlay={() => {
                                  const video = videoRefs.current[index];
                                  if (video && !isRevealed) {
                                    video.play().catch(err => {
                                      console.error(`Error playing video ${index}:`, err);
                                    });
                                  }
                                }}
                              >
                                <source src="/images/reward_animation.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            </div>
                            
                            {/* Back side - Reward content */}
                            {isRevealed && (
                              <div style={{
                                position: 'absolute',
                                width: '390px',
                                height: '390px',
                                left: '50%',
                                transform: 'translateX(-50%) rotateY(180deg)',
                                padding: enrichedOrder ? '0' : '20px 5px',
                                backgroundColor: enrichedOrder ? 'transparent' : 'rgba(0, 0, 0, 0.8)',
                                border: 'none',
                                borderRadius: enrichedOrder ? '0' : '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                boxSizing: 'border-box',
                                backfaceVisibility: 'hidden',
                                WebkitBackfaceVisibility: 'hidden',
                                overflow: 'visible'
                              }}>
                                {enrichedOrder ? (
                                  <div style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    position: 'relative',
                                    overflow: 'visible',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    margin: 0,
                                    padding: 0
                                  }}>
                                    <div className={`reward-order-container rarity-${rarity}`} style={{
                                      width: '390px',
                                      height: '390px',
                                      position: 'relative',
                                      margin: 0,
                                      padding: 0
                                    }}>
                                      <OrderDesign
                                        order={enrichedOrder}
                                        minimal={false}
                                        noMargin={true}
                                        gameState={null}
                                      />
                                      <OrderCardBubbles rarity={enrichedOrder?.rarity || rarity || 'common'} />
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ color: '#fff', textAlign: 'center' }}>
                                    <p>{order.name || 'Order'}</p>
                                    <p style={{ fontSize: '14px', color: '#aaa' }}>{rarity}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!previewMode && (
                <div className="spoils-actions">
                  <button
                    className="spoils-claim-button"
                    onClick={handleClaimSpoils}
                    disabled={claiming || spoils.length === 0 || grantedOrders.length > 0}
                  >
                    {claiming ? 'Claiming...' : grantedOrders.length > 0 ? 'Rewards Revealed' : 'Claim Spoils'}
                  </button>
                </div>
              )}
              {previewMode && (
                <>
                  <div className="spoils-actions">
                    <button
                      className="spoils-claim-button"
                      onClick={handleSimulateClaimSpoils}
                      disabled={claiming || spoils.length === 0}
                    >
                      {claiming ? 'Simulating...' : '🧪 Simulate Claim Spoils'}
                    </button>
                  </div>
                  
                  {/* Show result right after button in preview mode */}
                  {claimResult && !claimResult.error && grantedOrders.length === 0 && (
                    <div 
                      className={`spoils-claim-result ${claimResult.error ? 'error' : 'success'}`}
                      style={{ 
                        marginTop: '20px',
                        marginBottom: '20px',
                        padding: '20px',
                        borderRadius: '8px',
                        border: claimResult.error ? '2px solid rgba(200, 0, 0, 0.8)' : '2px solid rgba(0, 200, 0, 0.8)',
                        backgroundColor: claimResult.error ? 'rgba(200, 0, 0, 0.3)' : 'rgba(0, 200, 0, 0.3)',
                        color: '#fff',
                        fontSize: '16px'
                      }}
                    >
                      <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                        ✅ Spoils claimed successfully! (SIMULATED)
                      </p>
                      {claimResult.totalGranted !== undefined && (
                        <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                          <strong>Total Orders Granted: {claimResult.totalGranted}</strong>
                        </p>
                      )}
                      {claimResult.granted && (
                        <div className="spoils-granted-details">
                          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Granted by Rarity:</p>
                          <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>Common: {claimResult.granted.common || 0}</li>
                            <li>Rare: {claimResult.granted.rare || 0}</li>
                            <li>Epic: {claimResult.granted.epic || 0}</li>
                            <li>Legendary: {claimResult.granted.legendary || 0}</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Reward Cards Display */}
                  {grantedOrders.length > 0 && (
                    <div style={{ marginTop: '30px', marginBottom: '20px' }}>
                      <h3 style={{ color: '#fff', textAlign: 'center', marginBottom: '20px', fontSize: '20px' }}>
                        Click cards to reveal your rewards!
                      </h3>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '20px',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        {grantedOrders.map((order, index) => {
                          const isRevealed = revealedRewards[index];
                          const enrichedOrder = enrichedRewards[index];
                          const rarity = order.rarity?.toLowerCase() || 'common';
                          
                          return (
                            <div
                              key={order.id || index}
                              onClick={async () => {
                                if (!isRevealed) {
                                  setRevealedRewards(prev => {
                                    const newState = [...prev];
                                    newState[index] = true;
                                    return newState;
                                  });
                                  const isLegendary = (order.rarity || '').toLowerCase() === 'legendary';
                                  playSoundEffect(isLegendary ? '/sounds/legendary_reveal.mp3' : '/sounds/order_reveal.mp3');
                                  
                                  // Fetch full order data
                                  if (order.id) {
                                    const token = localStorage.getItem('token');
                                    try {
                                      const res = await fetch(`${API_URL}/all-orders?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
                                        headers: {
                                          Authorization: `Bearer ${token}`
                                        }
                                      });
                                      if (res.ok) {
                                        const allOrders = await res.json();
                                        const fullOrder = allOrders.find(o => o.id === order.id);
                                        if (fullOrder) {
                                          fullOrder.rarity = order.rarity || fullOrder.rarity;
                                          setEnrichedRewards(prev => {
                                            const newState = [...prev];
                                            newState[index] = fullOrder;
                                            return newState;
                                          });
                                        }
                                      }
                                    } catch (err) {
                                      console.error('Failed to fetch full order data:', err);
                                    }
                                  }
                                }
                              }}
                              style={{
                                width: '250px',
                                height: '380px',
                                margin: '10px',
                                perspective: '1000px',
                                cursor: !isRevealed ? 'pointer' : 'default',
                                position: 'relative',
                                overflow: 'visible'
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'relative',
                                  transformStyle: 'preserve-3d',
                                  transition: 'transform 0.6s',
                                  transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'
                                }}
                              >
                                {/* Front side - Question mark with video */}
                                <div style={{
                                  position: 'absolute',
                                  width: '100%',
                                  height: '100%',
                                  padding: '20px 5px',
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                  border: '2px solid rgba(255, 255, 255, 0.3)',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  boxSizing: 'border-box',
                                  backfaceVisibility: 'hidden',
                                  WebkitBackfaceVisibility: 'hidden',
                                  transform: 'rotateY(0deg)',
                                  overflow: 'hidden'
                                }}>
                                  <video
                                    ref={el => { videoRefs.current[index] = el; }}
                                    poster="/images/reward_intel.jpg"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    style={{
                                      position: 'absolute',
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      top: 0,
                                      left: 0,
                                      zIndex: 1,
                                      pointerEvents: 'none',
                                      backgroundColor: 'rgba(0, 0, 0, 0.8)'
                                    }}
                                    onCanPlay={() => {
                                      const video = videoRefs.current[index];
                                      if (video && !isRevealed) {
                                        video.play().catch(err => {
                                          console.error(`Error playing video ${index}:`, err);
                                        });
                                      }
                                    }}
                                  >
                                    <source src="/images/reward_animation.mp4" type="video/mp4" />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                                
                                {/* Back side - Reward content */}
                                {isRevealed && (
                                  <div style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    padding: enrichedOrder ? '0' : '20px 5px',
                                    backgroundColor: enrichedOrder ? 'transparent' : 'rgba(0, 0, 0, 0.8)',
                                    border: enrichedOrder ? 'none' : '2px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: enrichedOrder ? '0' : '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    boxSizing: 'border-box',
                                    backfaceVisibility: 'hidden',
                                    WebkitBackfaceVisibility: 'hidden',
                                    transform: 'rotateY(180deg)',
                                    overflow: 'hidden'
                                  }}>
                                    {enrichedOrder ? (
                                      <div className={`reward-order-card rarity-${rarity}`} style={{ position: 'relative' }}>
                                        <OrderDesign
                                          order={enrichedOrder}
                                          minimal={false}
                                          noMargin={true}
                                        />
                                        <OrderCardBubbles rarity={enrichedOrder.rarity || rarity || 'common'} />
                                      </div>
                                    ) : (
                                      <div style={{ color: '#fff', textAlign: 'center' }}>
                                        <p>{order.name || 'Order'}</p>
                                        <p style={{ fontSize: '14px', color: '#aaa' }}>{rarity}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="spoils-preview-note">
                    <p>✨ This preview shows what you would receive if the season ended right now.</p>
                    <p>💡 Keep playing to increase your rewards!</p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WarEffortSpoilsDialog;

